import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { getCity } from '../data/cityData';
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/services/full-car-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}];
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const city = slug ? getCity(slug) : undefined;
  if (!city) {
    return <Navigate to="/" replace data-id="element-541" data-mp="15eubs9nyml0r" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = `/ventura-county/${city.slug}`;
  const schema = {
    '@context': 'https://schema.org',
    '@type': ['AutoRepair', 'LocalBusiness'],
    name: `Mike's Pro Detail — Mobile Detailing ${city.name}`,
    image: 'https://cdn.magicpatterns.com/uploads/8ujQRT1VS2FomWSs5Jvzhk/mikesprodetail2026logo.png',
    description: city.metaDescription,
    telephone: '+18183819905',
    priceRange: '$160-$350',
    url: `${baseUrl}${path}`,
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: city.name,
      addressRegion: 'CA',
      addressCountry: 'US'
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Service Areas',
    item: `${baseUrl}/#service-areas`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} data-id="element-542" data-mp="liqitcbn7xuc">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-543" data-mp="1sv92ajj1ppty" data-mpi={i}>{p}</p>)}

      <h2 data-id="element-544" data-mp="1sug446y5k8nf">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-545" data-mp="1nxppzp5wrn4n">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-546" data-mp="1xobykzf7jddu">
        <li className="flex items-start" data-id="element-547" data-mp="19xeto6ltxgc3">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-548" data-mp="19y7u6pu739ls" />
          <span data-id="element-549" data-mp="14yv7q678k4jq">
            <strong data-id="element-550" data-mp="14zdfo039g77h">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-551" data-mp="1evzx0ewdog4r">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-552" data-mp="1euyddm1aqlbf" />
          <span data-id="element-553" data-mp="2uc8hiay4aks8">
            <strong data-id="element-554" data-mp="2uboyss0n7sd5">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-555" data-mp="2xuxvb51p7nx9">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-556" data-mp="2xtw8k3bblczp" />
          <span data-id="element-557" data-mp="32vbqhmi7b3dv">
            <strong data-id="element-558" data-mp="32vv9z7ni1zu0">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-559" data-mp="2svb7k1o8upa7">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-560" data-mp="2swcr6ujbsk3j" />
          <span data-id="element-561" data-mp="3gn03ds09xvvw">
            <strong data-id="element-562" data-mp="3gmgx58jzkvxp">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-563" data-mp="36v41s6yzbnmx">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-564" data-mp="3bup4msh9sg4x">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and power.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-565" data-mp="2gmoiyp8a9pv2">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-566" data-mp="2bnv2qp1m3kr7">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-567" data-mp="26pjuujuvnkbm" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-568" data-mp="21qz2t7ps46y9">Explore Our Detailing Services</h2>
      <p data-id="element-569" data-mp="2zdfzqbuloijh">
        Learn more about each of our professional mobile detailing services
        available in {city.name} and throughout Ventura County:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-570" data-mp="2ueuyqa7ixutz">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-571" data-mp="2pm1udnfywoif">
            {s.label} &rarr;
          </Link>)}
      </div>
    </ServicePageLayout>;
}